import { useState, type FormEvent } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CardActionArea from "@mui/material/CardActionArea";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Person from "@mui/icons-material/Person";
import Add from "@mui/icons-material/Add";
import VpnKey from "@mui/icons-material/VpnKey";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { errorMessage } from "../i18n/apiErrors";
import AppHead from "../components/AppHead";
import PageWrap from "../components/PageWrap";
import Panel from "../components/Panel";

// ---------------------------------------------------------------------------
// Form error state
// ---------------------------------------------------------------------------

/**
 * Formular-Fehler werden als sprach-unabhängiges Objekt gehalten und erst beim
 * Rendern aufgelöst (Req 12.6):
 * - lokale Validierungsfehler → `{ kind: "key" }` mit einem Katalog-Schlüssel
 * - API-Fehler → `{ kind: "api" }` mit dem Fehler-OBJEKT (via errorMessage)
 */
type FormError =
  | { kind: "key"; key: string }
  | { kind: "api"; err: unknown };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TransportMode = "bike" | "car" | "bus";

/**
 * Per-direction default trip details stored on a Group (migration 011).
 * Mirrors backend/src/schemas/entities.ts TripDefaults.
 */
interface TripDefaults {
  startTime: string | null;
  transportMode: TransportMode;
  meetingPoint: string | null;
}

interface Group {
  id: string;
  name: string;
  invitationCode: string;
  createdBy: string;
  createdAt: string;
  /** Default trip details for the morning direction (migration 011) */
  morningDefault: TripDefaults;
  /** Default trip details for the afternoon direction (migration 011) */
  afternoonDefault: TripDefaults;
}

interface GroupsResponse {
  groups: Group[];
}

// ---------------------------------------------------------------------------
// Group-default summary (read-only presentational, Req 7)
// ---------------------------------------------------------------------------

/**
 * True when a direction's default has anything worth showing. Encodes the
 * Empty_Field sentinels exactly as the Trip model and the backend use them
 * (start_time IS NULL, meeting_point IS NULL, transport_mode = 'bike'), so the
 * card's show/hide decision stays consistent with the rest of the feature.
 * Exported so the component test (Task 12.5) can exercise it directly.
 */
export function isNonEmptyDefault(def: TripDefaults): boolean {
  return (
    def.startTime !== null ||
    def.meetingPoint !== null ||
    def.transportMode !== "bike"
  );
}

/**
 * Non-interactive, read-only summary of a Group's per-direction defaults shown
 * below the group name inside the card link (Req 7.1–7.3, 7.6). Only
 * Typography/Box/Stack are used — no focusable control — so it stays passive
 * content inside the CardActionArea and never breaks the card-as-link
 * semantics. Transport is conveyed by the reused `weeklyPlan.transport.*`
 * labels, which already pair a glyph with text (never color alone).
 */
export function GroupDefaultSummary({
  morning,
  afternoon,
}: {
  morning: TripDefaults;
  afternoon: TripDefaults;
}) {
  const { t } = useI18n();

  const rows: { label: string; text: string }[] = (
    [
      { def: morning, labelKey: "weeklyPlan.direction.morning" },
      { def: afternoon, labelKey: "weeklyPlan.direction.afternoon" },
    ] as const
  )
    .filter(({ def }) => isNonEmptyDefault(def))
    .map(({ def, labelKey }) => {
      const parts = [
        def.startTime,
        t(`weeklyPlan.transport.${def.transportMode}`),
        def.meetingPoint,
      ].filter(Boolean) as string[];
      return { label: t(labelKey), text: parts.join(" · ") };
    });

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {t("groupList.defaults.label")}
      </Typography>
      <Stack gap={0.25}>
        {rows.map((row) => (
          <Typography key={row.label} variant="body2" color="text.secondary">
            {row.label}: {row.text}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Inline panel types
// ---------------------------------------------------------------------------

type ActivePanel = "none" | "create" | "join";

// ---------------------------------------------------------------------------
// Create Group Form
// ---------------------------------------------------------------------------

function CreateGroupForm({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useI18n();
  const [groupName, setGroupName] = useState("");
  // Fehler-Objekt halten; Text wird beim Rendern aufgelöst (Req 12.6).
  const [error, setError] = useState<FormError | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (name: string) =>
      apiClient.post<{ group: Group }>("/api/groups", { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      onSuccess();
    },
    onError: (err) => {
      setError({ kind: "api", err });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = groupName.trim();
    if (!trimmed) {
      setError({ kind: "key", key: "groupList.create.nameRequired" });
      return;
    }
    mutation.mutate(trimmed);
  }

  return (
    <Panel>
      <Typography variant="h6" fontWeight={600} mb={2}>
        {t("groupList.create.title")}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack gap={1.5}>
          <TextField
            id="group-name"
            type="text"
            label={t("groupList.create.nameLabel")}
            placeholder={t("groupList.create.namePlaceholder")}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            disabled={mutation.isPending}
            autoFocus
            fullWidth
          />
          <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={mutation.isPending}
              sx={{ minHeight: 44 }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={mutation.isPending}
              aria-busy={mutation.isPending}
              sx={{ minHeight: 44 }}
            >
              {mutation.isPending
                ? t("groupList.create.submitting")
                : t("groupList.create.submit")}
            </Button>
          </Stack>
          {error !== null && (
            <Alert severity="error" role="alert">
              {error.kind === "key" ? t(error.key) : errorMessage(t, error.err)}
            </Alert>
          )}
        </Stack>
      </Box>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Join Group Form
// ---------------------------------------------------------------------------

function JoinGroupForm({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useI18n();
  const [invitationCode, setInvitationCode] = useState("");
  // Fehler-Objekt halten; Text wird beim Rendern aufgelöst (Req 12.6).
  const [error, setError] = useState<FormError | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (code: string) =>
      apiClient.post<{ group: Group }>("/api/groups/join", {
        invitationCode: code,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      onSuccess();
    },
    onError: (err) => {
      setError({ kind: "api", err });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = invitationCode.trim().toUpperCase();
    if (!trimmed) {
      setError({ kind: "key", key: "groupList.join.codeRequired" });
      return;
    }
    mutation.mutate(trimmed);
  }

  return (
    <Panel>
      <Typography variant="h6" fontWeight={600} mb={2}>
        {t("groupList.join.title")}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack gap={1.5}>
          <TextField
            id="invitation-code"
            type="text"
            label={t("groupList.join.codeLabel")}
            placeholder={t("groupList.join.codePlaceholder")}
            value={invitationCode}
            onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
            disabled={mutation.isPending}
            autoFocus
            fullWidth
            inputProps={{
              maxLength: 8,
              style: { textTransform: "uppercase", letterSpacing: "0.1em" },
            }}
          />
          <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={onClose}
              disabled={mutation.isPending}
              sx={{ minHeight: 44 }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={mutation.isPending}
              aria-busy={mutation.isPending}
              sx={{ minHeight: 44 }}
            >
              {mutation.isPending
                ? t("groupList.join.submitting")
                : t("groupList.join.submit")}
            </Button>
          </Stack>
          {error !== null && (
            <Alert severity="error" role="alert">
              {error.kind === "key" ? t(error.key) : errorMessage(t, error.err)}
            </Alert>
          )}
        </Stack>
      </Box>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// GroupList page
// ---------------------------------------------------------------------------

export default function GroupList() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [activePanel, setActivePanel] = useState<ActivePanel>("none");

  const { data, isLoading, error: fetchError } = useQuery<GroupsResponse>({
    queryKey: ["groups"],
    queryFn: () => apiClient.get<GroupsResponse>("/api/groups"),
  });

  const groups = data?.groups ?? [];

  function openPanel(panel: ActivePanel) {
    setActivePanel((prev) => (prev === panel ? "none" : panel));
  }

  function closePanel() {
    setActivePanel("none");
  }

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  return (
    <>
      {/* Sticky Kopfzeile: Marken-Logo statt Text-Titel, Profil-Button und
          DE/EN-Umschalter. Es gibt eine helle und eine dunkle AppBar-Variante
          in frontend/public/; AppHead wählt anhand des Theme-Modus. */}
      <AppHead
        title={t("groupList.title")}
        logoSrc="/se-logo-appbar-hell.png"
        logoSrcDark="/se-logo-appbar-dunkel.png"
      >
        <Button
          component={RouterLink}
          to="/profile"
          color="inherit"
          startIcon={<Person />}
          aria-label={t("groupList.profileAria")}
          sx={{
            minWidth: 44,
            minHeight: 44,
            flexShrink: 0,
            textAlign: "left",
            textTransform: "none",
            lineHeight: 1.1,
            // Mobil nur das Icon: kein Icon-Margin, damit es sauber zentriert.
            "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: 0 },
          }}
        >
          {/* Textspalte erst ab sm: auf dem Smartphone bleibt nur das Icon,
              damit Header, Logo und Sprachumschalter nicht überlagern. */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              flexDirection: "column",
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <Typography component="span" fontWeight={600} fontSize={15} lineHeight={1.2}>
              {t("groupList.action.profile")}
            </Typography>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              lineHeight={1.1}
              noWrap
            >
              {t("groupList.profileSubtitle")}
            </Typography>
          </Box>
        </Button>
      </AppHead>

      <PageWrap>
        {/* Aktionskacheln: Icon + fetter Titel + Hinweis. Aktives Panel gefüllt,
            sonst outline. Hinweiszeile ab sm sichtbar. */}
        <Stack direction="row" gap={1} mb={2}>
          <Button
            color="primary"
            variant={activePanel === "create" ? "contained" : "outlined"}
            onClick={() => openPanel("create")}
            aria-expanded={activePanel === "create"}
            sx={{
              flex: 1,
              minHeight: 52,
              py: 1,
              px: 1.5,
              borderRadius: 3,
              justifyContent: "flex-start",
              textAlign: "left",
              lineHeight: 1.2,
              gap: 1,
            }}
          >
            <Add sx={{ fontSize: 24, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography component="span" display="block" fontWeight={700} lineHeight={1.2}>
                {t("groupList.action.createGroup")}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                sx={{ display: { xs: "none", sm: "block" }, opacity: 0.7, fontWeight: 400 }}
              >
                {t("groupList.action.createHint")}
              </Typography>
            </Box>
          </Button>
          <Button
            color="secondary"
            variant={activePanel === "join" ? "contained" : "outlined"}
            onClick={() => openPanel("join")}
            aria-expanded={activePanel === "join"}
            sx={{
              flex: 1,
              minHeight: 52,
              py: 1,
              px: 1.5,
              borderRadius: 3,
              justifyContent: "flex-start",
              textAlign: "left",
              lineHeight: 1.2,
              gap: 1,
            }}
          >
            <VpnKey sx={{ fontSize: 24, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography component="span" display="block" fontWeight={700} lineHeight={1.2}>
                {t("groupList.action.joinGroup")}
              </Typography>
              <Typography
                component="span"
                variant="caption"
                sx={{ display: { xs: "none", sm: "block" }, opacity: 0.7, fontWeight: 400 }}
              >
                {t("groupList.action.joinHint")}
              </Typography>
            </Box>
          </Button>
        </Stack>

        {/* Inline form panels */}
        {activePanel === "create" && (
          <CreateGroupForm onClose={closePanel} onSuccess={closePanel} />
        )}
        {activePanel === "join" && (
          <JoinGroupForm onClose={closePanel} onSuccess={closePanel} />
        )}

        {/* Loading state */}
        {isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }} aria-live="polite">
            <CircularProgress aria-label={t("groupList.loading")} />
          </Stack>
        )}

        {/* Fetch error */}
        {!isLoading && fetchError !== null && (
          <Alert severity="error" role="alert">
            {errorMessage(t, fetchError)}
          </Alert>
        )}

        {/* Group list */}
        {!isLoading && fetchError === null && groups.length > 0 && (
          <>
            {groups.map((group) => (
              // Ganze Karte ist der Link zum Plan; CardActionArea trägt die
              // Polsterung, der Panel-Card bleibt dafür padding-los (p:0).
              <Panel key={group.id} sx={{ mb: 1.5, p: 0 }}>
                <CardActionArea
                  component={RouterLink}
                  to={`/groups/${group.id}/plan`}
                  aria-label={t("groupList.viewPlanAria", { name: group.name })}
                  sx={{ p: 2, minHeight: 64 }}
                >
                  <Stack gap={1}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Typography
                        fontWeight={700}
                        fontSize={18}
                        sx={{ minWidth: 0, wordBreak: "break-word" }}
                      >
                        {group.name}
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap={0.5}
                        sx={{ flexShrink: 0 }}
                      >
                        <Typography
                          sx={{
                            color: "primary.main",
                            display: { xs: "none", sm: "block" },
                          }}
                        >
                          {t("groupList.viewPlan")}
                        </Typography>
                        <ChevronRight sx={{ color: "primary.main" }} />
                      </Stack>
                    </Stack>

                    {/* Read-only group-default summary, only when a default is
                        set (Req 7.1/7.2) */}
                    {(isNonEmptyDefault(group.morningDefault) ||
                      isNonEmptyDefault(group.afternoonDefault)) && (
                      <GroupDefaultSummary
                        morning={group.morningDefault}
                        afternoon={group.afternoonDefault}
                      />
                    )}
                  </Stack>
                </CardActionArea>
              </Panel>
            ))}
          </>
        )}

        {/* Empty state */}
        {!isLoading && fetchError === null && groups.length === 0 && (
          <Panel sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" fontWeight={700} mb={0.5}>
              {t("groupList.empty.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("groupList.empty.hint")}
            </Typography>
          </Panel>
        )}

        {/* Logout row */}
        {user !== null && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1.5}
            mt={3}
          >
            <Typography variant="body2" color="text.secondary">
              {user.name}
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleLogout}
              sx={{ minHeight: 44 }}
            >
              {t("groupList.signOut")}
            </Button>
          </Stack>
        )}
      </PageWrap>
    </>
  );
}
